<template>
  <Layout
    :title="title"
    :items="items"
  >
    <b-card v-if="isLoading && !title">
      <Skeleton height="60px" />
      <div class="d-lg-flex justify-content-between mt-4 mx-0 gap-4">
        <Skeleton height="25px" />
        <Skeleton height="25px" />
        <Skeleton height="25px" />
        <Skeleton height="25px" />
      </div>
    </b-card>
    <b-card v-else>
      <template #header>
        <h4 class="card-title">
          {{ buildingDetail.name }}
        </h4>
      </template>
      <div class="d-lg-flex justify-content-between mx-0">
        <div class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-info shadow-sm mb-2">
          <div class="fw-bold ps-3 py-1 bg-white">
            Building Area
          </div>
          <div class="fw-bold px-2 py-1 bg-white">
            {{ formatNumber(buildingArea) }} m<sup>2</sup>
          </div>
        </div>

        <div
          v-if="selectedMode != 'supply'"
          class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-info shadow-sm mb-2"
        >
          <div class="fw-bold ps-3 py-1 bg-white">
            Baseline Consumption per Month
          </div>
          <div class="fw-bold px-2 py-1 bg-white">
            {{ formatNumber(baselineConsumption) }} kWh
          </div>
        </div>
        <div
          v-if="selectedMode != 'supply'"
          class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-info shadow-sm mb-2"
        >
          <div class="fw-bold ps-3 py-1 bg-white">
            Energy Target  (Up to Now)
          </div>
          <div class="fw-bold px-2 py-1 bg-white">
            {{ formatNumber(energyTarget) }} kWh
          </div>
        </div>

        <div
          v-if="selectedMode == 'supply'"
          class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-info shadow-sm mb-2"
        >
          <div class="fw-bold ps-3 py-1 bg-white">
            Monthly Energy Generated Target
          </div>
          <div class="fw-bold px-2 py-1 bg-white">
            {{ formatNumber(energyTargetSupply) }} kWh
          </div>
        </div>
        <div
          v-if="selectedMode == 'supply'"
          class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-info shadow-sm mb-2"
        >
          <div class="fw-bold ps-3 py-1 bg-white">
            Daily Energy Generated Target
          </div>
          <div class="fw-bold px-2 py-1 bg-white">
            {{ formatNumber(energyTargetSupplyDaily) }} kWh
          </div>
        </div>

        <div class="d-flex border border-4 border-top-0 border-end-0 border-bottom-0 border-info shadow-sm mb-2">
          <div class="fw-bold ps-3 py-1 bg-white">
            Consumption per Area (last month)
          </div>
          <div class="fw-bold px-2 py-1 bg-white">
            {{ formatNumber(lastMonthData / buildingArea) }} kWh/m<sup>2</sup>
          </div>
        </div>
      </div>
    </b-card>
    <b-nav
      tabs
      class="mt-1 d-print-none"
    >
      <b-nav-item
        :active="selectedMode == 'demand'"
        :to="`/unit/${unitId}/building/${buildingId}/demand`"
        class="d-flex"
      >
        <span class="fw-bold">
          DEMAND
        </span>
      </b-nav-item>
      <b-nav-item
        :active="selectedMode == 'supply'"
        :to="`/unit/${unitId}/building/${buildingId}/supply`"
      >
        <span class="fw-bold">
          SUPPLY
        </span>
      </b-nav-item>
      <b-nav-item
        :active="selectedMode == 'total'"
        :to="`/unit/${unitId}/building/${buildingId}/total`"
      >
        <span class="fw-bold">
          TOTAL CONSUMED
        </span>
      </b-nav-item>
    </b-nav>
    <div
      class="p-3 mb-3"
      style="background-color: #eff2f7"
    >
      <BuildingDetailDemand
        v-if="selectedMode == 'demand'"
        :is-loading="isLoading"
        :energy-target="energyTarget"
        :weekday-limit="weekdayLimit"
        :weekend-limit="weekendLimit"
        :update-counter="updateCounter"
        :peak-limit="peakLimit"
        :devices="buildingDevices"
      />
      <BuildingDetailSupply
        v-if="selectedMode == 'supply'"
        :is-loading="isLoading"
        :update-counter="updateCounter"
        :energy-target="energyTargetSupply"
        :devices="buildingDevices"
      />
      <BuildingDetailTotal
        v-if="selectedMode == 'total'"
        :is-loading="isLoading"
        :energy-target="energyTarget"
        :weekday-limit="weekdayLimit"
        :weekend-limit="weekendLimit"
        :update-counter="updateCounter"
        :peak-limit="peakLimit"
        :devices="buildingDevices"
      />
    </div>
  </Layout>
</template>

<script>
import {
  addMonths, eachWeekendOfMonth, getDaysInMonth, eachDayOfInterval, startOfMonth, lastDayOfMonth, isWeekend,
} from 'date-fns';
import Skeleton from '@/components/Skeleton';
import appConfig from '@/app.config';
import Echo from '@/services/web-socket';
import Layout from '@/layouts/main';
import BuildingDetailDemand from './BuildingDetailDemand.vue';
import BuildingDetailSupply from './BuildingDetailSupply.vue';
import BuildingDetailTotal from './BuildingDetailTotal.vue';
import { convertToKwh, formatNumber } from '@/utils/helper';

export default {
  page: {
    title: 'Building Detail',
    meta: [
      {
        name: 'description',
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    Skeleton,
    BuildingDetailDemand,
    BuildingDetailSupply,
    BuildingDetailTotal,
  },

  data() {
    return {
      isLoading: false,
      unitDetail: {},
      buildingDetail: {
        devices: [],
      },
      lastMonthPayload: [],
      updateCounter: 0,
    };
  },
  computed: {
    buildingId() {
      return this.$route.params.buildingId;
    },
    unitId() {
      return this.$route.params.unitId;
    },
    selectedMode() {
      return this.$route.params.mode;
    },
    selectedOrganizationDetail() {
      return this.$store.state.organization.selectedOrganizationDetail;
    },
    title() {
      return this.buildingDetail.name;
    },
    items() {
      return [
        {
          text: 'HOME',
          to: '/home',
        },
        {
          text: this.selectedOrganizationDetail?.name ?? '',
          to: '/campus-map',
        },
        {
          text: this.unitDetail?.name ?? '',
          to: `/unit/${this.unitId}`,
        },
        {
          text: this.buildingDetail?.name ?? '',
          active: true,
        },
      ];
    },
    buildingArea() {
      return this.buildingDetail?.custom_attributes?.building_area ?? '-';
    },
    baselineConsumption() {
      return this.buildingDetail?.custom_attributes?.monthly_energy_budget ?? '-';
    },
    peakLimit() {
      return Number(this.buildingDetail?.custom_attributes?.maximum_peak_power) ?? 0;
    },
    energyTarget() {
      // Total daily limit until today
      const today = new Date();
      const firstDayOfMonth = startOfMonth(today);
      const allDaysUntilToday = eachDayOfInterval({ start: firstDayOfMonth, end: today });

      const totalWeekdaysUntilToday = allDaysUntilToday.filter((day) => !isWeekend(day)).length;
      const totalWeekendsUntilToday = allDaysUntilToday.filter((day) => isWeekend(day)).length;
      const totalTarget = totalWeekdaysUntilToday * this.weekdayLimit + totalWeekendsUntilToday * this.weekendLimit;
      return Number(totalTarget.toFixed(0)) ?? 0;
    },
    energyTargetSupply() {
      return Number(this.buildingDetail?.custom_attributes?.monthly_generated_energy ?? 0);
    },
    energyTargetSupplyDaily() {
      const totalDaysInMonth = getDaysInMonth(new Date());
      return Number(this.energyTargetSupply / totalDaysInMonth).toFixed(2);
    },
    weekdayLimit() {
      if (Number(this.baselineConsumption)) {
        const monthlyLimit = Number(this.baselineConsumption);
        const totalDaysInMonth = getDaysInMonth(new Date());
        const totalWeekendsInMonth = eachWeekendOfMonth(new Date()).length;
        const totalWeekdaysInMonth = totalDaysInMonth - totalWeekendsInMonth;

        // weekendLimit = 1/2 weekdayLimit
        // Formula
        // monthlyLimit = totalWeekdays * weekdayLimit + totalWeekends * weekendLimit
        // monthlyLimit = totalWeekdays * weekdayLimit + totalWeekends * weekdayLimit / 2
        // monthlyLimit = weekdayLimit ( totalWeekdays + totalWeekends / 2 )
        // weekdayLimit = monthlyLimit / ( totalWeekdays + totalWeekends / 2 )
        const weekdayLimit = monthlyLimit / (totalWeekdaysInMonth + totalWeekendsInMonth / 2);
        return Number(weekdayLimit.toFixed(2));
      }
      return 0;
    },
    weekendLimit() {
      return Number((this.weekdayLimit / 2).toFixed(2));
    },
    lastMonthData() {
      if (this.lastMonthPayload) {
        const flattenData = this.lastMonthPayload.map((data) => Number(data.cumulative_value));
        return convertToKwh(flattenData.reduce((a, b) => a + b, 0));
      }
      return 0;
    },
    buildingDevices() {
      const { devices } = this.buildingDetail;
      if (this.selectedMode === 'demand') {
        return devices.filter((device) => Object.keys(device.latest_payload).includes('Active_Energy_Demand_Consumed') ?? false);
      } if (this.selectedMode === 'supply') {
        return devices.filter((device) => Object.keys(device.latest_payload).includes('Active_Energy_Supply_Consumed') ?? false);
      }
      return devices;
    },
  },
  watch: {
    $route: {
      deep: true,
      immediate: true,
      handler() {
        if (this.$route.params.mode !== 'total') {
          if (this.$route.params.mode !== 'supply') {
            if (this.$route.params.mode !== 'demand') {
              this.$router.push(`/unit/${this.unitId}/building/${this.buildingId}/demand`);
            }
          }
        }
        this.getBuildingDetail();
      },
    },
  },
  beforeDestroy() {
    // leave channel when leaving page
    this.unsubscribePayload();
  },
  methods: {
    formatNumber,
    async getUnitDetail() {
      try {
        const { data } = await this.$api.getUnitDetail(this.unitId);
        this.unitDetail = data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getBuildingDetail() {
      try {
        this.isLoading = true;
        await this.getUnitDetail();
        const { data } = await this.$api.getBuildingDetail(this.buildingId);
        this.buildingDetail = data.data;
        this.subscribePayload();
        this.getLastMonthData();
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getLastMonthData() {
      const startLastMonth = addMonths(startOfMonth(new Date()), -1);
      const lastDay = lastDayOfMonth(startLastMonth);
      const endLastMonth = new Date(lastDay.setHours(23, 59));
      try {
        this.isLoading = true;
        const { data } = await this.$api.getUnitEnergy(this.unitId,
          {
            per_page: 200,
            block_id: this.buildingId,
            interval: 'monthly',
            start_date: startLastMonth.toISOString(),
            end_date: endLastMonth.toISOString(),
            timezone: 'Asia/Jakarta',
            payload_keys: ['Active_Energy_Demand_Consumed', 'Active_Energy_Supply_Consumed'],
            aggregate: 'sum',
          });
        this.lastMonthPayload = data.data;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    subscribePayload() {
      const buildingDevices = this.buildingDetail?.devices;
      buildingDevices.forEach((item) => this.subscribeDevice(item.device_id));
    },
    unsubscribePayload() {
      const buildingDevices = this.buildingDetail?.devices;
      buildingDevices.forEach((item) => (
        Echo.leaveChannel(`device.${item.device_id}`)
      ));
    },
    subscribeDevice(device_id) {
      Echo.subscribeChannel(`device.${device_id}`)
      // listen new payloads
        .listen('EnergyCalculated', (data) => {
          this.updateCounter += 1;
        });
    },
  },
};
</script>
